// Servizio per gestire i Daily Discoveries da Supabase
import { supabase } from '../config/supabase';
import geminiLogger from './geminiLogger';
import { searchDeezerArtist } from './deezer';
import { callGeminiViaProxy } from './geminiProxy';
import { getDailyDiscoveryConfig, getModelsForContext } from '../config/aiProvider';

// ============================================
// CONFIGURAZIONE PROMPT VERSION
// ============================================
const PROMPT_VERSION = 2; // 1 = vecchio, 2 = nuovo con auto-correzione bias

/** True solo se l'URL sembra una vera foto artista (non placeholder/vuoto) */
export const isUsableArtistImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const u = url.trim();
  if (!u || u === 'null' || u === 'undefined') return false;
  // Placeholder Deezer ufficiale (MD5 stringa vuota → silhouette grigia)
  // es. .../artist/d41d8cd98f00b204e9800998ecf8427e/1000x1000-000000-80-0-0.jpg
  if (/d41d8cd98f00b204e9800998ecf8427e/i.test(u)) return false;
  if (/1000x1000-000000-80-0-0/i.test(u)) return false;
  // Placeholder Deezer: path artist vuoto, /0/, hash assente
  if (/\/images\/artist\/\/+/i.test(u)) return false;
  if (/\/artist\/\/+/i.test(u) || /\/artist\/0\//i.test(u)) return false;
  if (/\/artist\/null/i.test(u)) return false;
  if (/placeholder|default.?image|no.?image|missing|blank|empty/i.test(u)) return false;
  return true;
};

class DailyDiscoveryService {
  /**
   * Ottiene l'approfondimento di oggi
   * @param {string} language - Lingua (it o en)
   * @returns {Promise<Object|null>} Discovery con artisti
   */
  async getTodayDiscovery(language = 'en') {
    // Prende sempre l'ultimo discovery generato (il più recente)
    // Questo funziona sia se ce n'è uno solo oggi, sia se ce ne sono multipli
          return this.getLatestDiscovery(language);
  }

  /**
   * Ottiene l'ultimo approfondimento disponibile
   * @param {string} language - Lingua (it o en)
   * @returns {Promise<Object|null>}
   */
  async getLatestDiscovery(language = 'en') {
    try {
      const { data: discovery, error: discoveryError } = await supabase
        .from('daily_discoveries')
        .select('*')
        .order('discovery_date', { ascending: false })
        .limit(1)
        .single();

      if (discoveryError || !discovery) {
        console.error('[DailyDiscovery] No discoveries available');
        return null;
      }

      // Carica SOLO gli artisti verificati (spotify_verified = true)
      // JOIN con artists per ottenere image_url (precaricata, evita skeleton iniziale)
      const { data: artists, error: artistsError } = await supabase
        .from('daily_discovery_artists')
        .select('artist_name, artist_genre, artist_origin, spotify_id, position, artists(image_url, deezer_id, deezer_url)')
        .eq('discovery_id', discovery.id)
        .eq('spotify_verified', true)
        .order('position', { ascending: true });

      if (artistsError) {
        console.error('[DailyDiscovery] Error loading artists:', artistsError);
      }

      // Usa i campi bilingue se disponibili
      const title = language === 'it' 
        ? (discovery.title_it || discovery.title) 
        : (discovery.title_en || discovery.title);
      
      const introduction = language === 'it' 
        ? (discovery.introduction_it || discovery.introduction) 
        : (discovery.introduction_en || discovery.introduction);

      return {
        id: discovery.id,
        title,
        theme_category: discovery.theme_category,
        introduction,
        tags: discovery.tags || [],
        discovery_date: discovery.discovery_date,
        artist_count: discovery.artist_count,
        artists: artists ? artists.map(a => {
          const linkedArtist = Array.isArray(a.artists) ? a.artists[0] : a.artists;
          if (a.artist_genre || a.artist_origin) {
            return {
              name: a.artist_name,
              genre: a.artist_genre,
              origin: a.artist_origin,
              spotify_id: a.spotify_id,
              image_url: linkedArtist?.image_url || null,
              deezer_id: linkedArtist?.deezer_id || null,
              deezer_url: linkedArtist?.deezer_url || null,
            };
          }
          return a.artist_name;
        }) : []
      };

    } catch (error) {
      console.error('[DailyDiscovery] Error in getLatestDiscovery:', error);
      throw error;
    }
  }

  /**
   * Ottiene un approfondimento specifico per ID
   * @param {number} discoveryId 
   * @param {string} language - Lingua (it o en)
   * @returns {Promise<Object|null>}
   */
  async getDiscoveryById(discoveryId, language = 'en') {
    try {
      const { data: discovery, error: discoveryError } = await supabase
        .from('daily_discoveries')
        .select('*')
        .eq('id', discoveryId)
        .single();

      if (discoveryError || !discovery) {
        console.warn(`[DailyDiscovery] Discovery ${discoveryId} not found (may have been deleted)`);
        // Pulisci localStorage se l'ID salvato non esiste più
        if (typeof window !== 'undefined') {
          localStorage.removeItem('lastViewedDiscoveryId');
          console.log('[DailyDiscovery] Cleared invalid discovery ID from memory');
        }
        return null;
      }

      // Carica SOLO gli artisti verificati (spotify_verified = true)
      // JOIN con artists per ottenere image_url (precaricata, evita skeleton iniziale)
      const { data: artists, error: artistsError } = await supabase
        .from('daily_discovery_artists')
        .select('artist_name, artist_genre, artist_origin, spotify_id, position, artists(image_url, deezer_id, deezer_url)')
        .eq('discovery_id', discovery.id)
        .eq('spotify_verified', true)
        .order('position', { ascending: true });

      if (artistsError) {
        console.error('[DailyDiscovery] Error loading artists:', artistsError);
      }

      // Usa i campi bilingue se disponibili
      const title = language === 'it' 
        ? (discovery.title_it || discovery.title) 
        : (discovery.title_en || discovery.title);
      
      const introduction = language === 'it' 
        ? (discovery.introduction_it || discovery.introduction) 
        : (discovery.introduction_en || discovery.introduction);

      return {
        id: discovery.id,
        title,
        theme_category: discovery.theme_category,
        introduction,
        tags: discovery.tags || [],
        discovery_date: discovery.discovery_date,
        artist_count: discovery.artist_count,
        artists: artists ? artists.map(a => {
          const linkedArtist = Array.isArray(a.artists) ? a.artists[0] : a.artists;
          if (a.artist_genre || a.artist_origin) {
            return {
              name: a.artist_name,
              genre: a.artist_genre,
              origin: a.artist_origin,
              spotify_id: a.spotify_id,
              image_url: linkedArtist?.image_url || null,
              deezer_id: linkedArtist?.deezer_id || null,
              deezer_url: linkedArtist?.deezer_url || null,
            };
          }
          return a.artist_name;
        }) : []
      };

    } catch (error) {
      console.error('[DailyDiscovery] Error in getDiscoveryById:', error);
      throw error;
    }
  }

  /**
   * Ottiene tutti gli approfondimenti (nessun limite)
   * @param {string} language - Lingua (it o en)
   * @returns {Promise<Array>} Lista di discoveries (senza artisti)
   */
  async getAllDiscoveries(language = 'en') {
    try {
      const { data: discoveries, error } = await supabase
        .from('daily_discoveries')
        .select('id, title, title_it, title_en, theme_category, discovery_date, artist_count, tags')
        .order('discovery_date', { ascending: false });

      if (error) {
        console.error('[DailyDiscovery] Error loading all discoveries:', error);
        return [];
      }

      const list = discoveries || [];
      if (list.length === 0) return [];

      // Copertina: artisti ordinati per position; prima image_url disponibile (1°, 2°, 3°…)
      const ids = list.map((d) => d.id);
      let coverRows = null;
      let coverError = null;

      ({ data: coverRows, error: coverError } = await supabase
        .from('daily_discovery_artists')
        .select('discovery_id, position, artist_name, artists!artist_id(image_url)')
        .in('discovery_id', ids)
        .order('position', { ascending: true }));

      // Fallback se la hint FK non è disponibile
      if (coverError) {
        console.warn('[DailyDiscovery] Cover join artist_id failed, retry plain artists():', coverError.message);
        ({ data: coverRows, error: coverError } = await supabase
          .from('daily_discovery_artists')
          .select('discovery_id, position, artist_name, artists(image_url)')
          .in('discovery_id', ids)
          .order('position', { ascending: true }));
      }

      if (coverError) {
        console.error('[DailyDiscovery] Error loading discovery covers:', coverError);
      }

      const artistsByDiscovery = {};
      (coverRows || []).forEach((row) => {
        if (!artistsByDiscovery[row.discovery_id]) {
          artistsByDiscovery[row.discovery_id] = [];
        }
        const linked = Array.isArray(row.artists) ? row.artists[0] : row.artists;
        artistsByDiscovery[row.discovery_id].push({
          name: row.artist_name || null,
          image_url: isUsableArtistImageUrl(linked?.image_url) ? linked.image_url : null,
        });
      });

      const coverByDiscovery = {};
      const candidatesByDiscovery = {};
      const coverUrlsByDiscovery = {};

      for (const d of list) {
        const artists = artistsByDiscovery[d.id] || [];
        candidatesByDiscovery[d.id] = artists
          .map((a) => a.name)
          .filter(Boolean);

        // Solo URL non-placeholder (filtro sync veloce, niente probe/Deezer qui)
        const urls = artists
          .map((a) => a.image_url)
          .filter(isUsableArtistImageUrl);
        coverUrlsByDiscovery[d.id] = urls;
        if (urls[0]) coverByDiscovery[d.id] = urls[0];
      }

      // Ritorna subito: eventuali cover mancanti le completa la wall in background
      return list.map((d) => ({
        ...d,
        title: language === 'it' ? (d.title_it || d.title) : (d.title_en || d.title),
        cover_image: coverByDiscovery[d.id] || null,
        cover_candidates: candidatesByDiscovery[d.id] || [],
        cover_urls: coverUrlsByDiscovery[d.id] || [],
      }));

    } catch (error) {
      console.error('[DailyDiscovery] Error in getAllDiscoveries:', error);
      return [];
    }
  }

  /**
   * Cerca approfondimenti per titolo o categoria
   * @param {string} searchTerm 
   * @returns {Promise<Array>}
   */
  async searchDiscoveries(searchTerm) {
    try {
      const { data: discoveries, error } = await supabase
        .from('daily_discoveries')
        .select('id, title, theme_category, discovery_date, artist_count, tags')
        .or(`title.ilike.%${searchTerm}%,theme_category.ilike.%${searchTerm}%`)
        .order('discovery_date', { ascending: false })
        .limit(20);

      if (error) {
        console.error('[DailyDiscovery] Error searching discoveries:', error);
        return [];
      }

      return discoveries || [];

    } catch (error) {
      console.error('[DailyDiscovery] Error in searchDiscoveries:', error);
      return [];
    }
  }

  /**
   * ============================================
   * HELPER: CONTEGGIO PAROLE
   * ============================================
   */
  countWords(text) {
    if (!text || typeof text !== 'string') return 0;
    // Rimuove caratteri speciali e conta le parole
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }


  /**
   * ============================================
   * HELPER: PROMPT PER TRADUZIONE/ADATTAMENTO ITALIANO
   * ============================================
   */
  buildTranslationPrompt(titleEn, introductionEn, themeCategory) {
    return `Sei un traduttore editoriale specializzato in giornalismo musicale.

Il tuo compito è TRADURRE E ADATTARE questo articolo musicale dall'inglese all'italiano.

⚠️ NON fare una traduzione letterale parola per parola.
⚠️ Fai un ADATTAMENTO CULTURALE di alta qualità.

════════════════════════════════════════
ARTICOLO ORIGINALE IN INGLESE
════════════════════════════════════════

TEMA: ${themeCategory}
TITOLO: ${titleEn}

TESTO:
${introductionEn}

════════════════════════════════════════
ISTRUZIONI PER L'ADATTAMENTO ITALIANO
════════════════════════════════════════

1. NATURALEZZA LINGUISTICA
   - Scrivi in italiano fluido e naturale, come se fosse stato scritto originalmente in italiano
   - Evita calchi linguistici dall'inglese
   - Usa costruzioni sintattiche italiane autentiche
   - NON tradurre parola per parola

2. TERMINOLOGIA MUSICALE
   - Mantieni i termini tecnici in inglese se di uso comune (es: "city pop", "krautrock", "drill")
   - Traduci i termini generici (es: "sound" → "suono", "scene" → "scena")
   - Usa la terminologia italiana quando più naturale

3. METAFORE E FIGURE RETORICHE
   - Adatta le metafore alla sensibilità italiana
   - Se una metafora inglese non funziona in italiano, trovane una equivalente
   - Mantieni lo stesso impatto emotivo e poetico

4. RIFERIMENTI CULTURALI
   - Se ci sono riferimenti culturali anglofoni difficili, adattali o spiegali brevemente
   - Mantieni i nomi propri (luoghi, artisti, album) come nell'originale

5. TONO E STILE
   - Mantieni lo stesso tono: appassionato, informato, accessibile
   - Preserva l'energia e il ritmo del testo originale
   - L'articolo deve avere la stessa capacità di coinvolgere il lettore

6. LUNGHEZZA
   - MANTIENI la stessa lunghezza: 300-400 parole
   - Se l'inglese ha 350 parole, anche l'italiano deve averne circa 350
   - Conta le parole mentre traduci

7. STRUTTURA
   - Mantieni la stessa divisione in paragrafi (usa \\n\\n)
   - Preserva la struttura tripartita: Apertura, Sviluppo, Chiusura

8. QUALITÀ EDITORIALE
   - Pensa come un editor di Rolling Stone Italia o di Blow Up
   - Il testo deve sembrare scritto da un giornalista musicale italiano esperto
   - Rileggi mentalmente: suona naturale in italiano?

════════════════════════════════════════
ESEMPIO DI COSA FARE E NON FARE
════════════════════════════════════════

❌ MALE (traduzione letterale):
"The synth-pop sound conquered the airwaves"
→ "Il suono synth-pop conquistò le onde radio"

✅ BENE (adattamento naturale):
"The synth-pop sound conquered the airwaves"
→ "Il synth-pop invase le frequenze radiofoniche"

❌ MALE (calco inglese):
"It was more than just a genre"
→ "Era più di solo un genere"

✅ BENE (italiano naturale):
"It was more than just a genre"
→ "Non era semplicemente un genere musicale"

════════════════════════════════════════
RISPONDI
════════════════════════════════════════

Rispondi SOLO con:
1. Il TITOLO italiano adattato (max 60 caratteri)
2. Una linea vuota
3. L'ARTICOLO italiano (300-400 parole, paragrafi separati da \\n\\n)

Formato:
[TITOLO ITALIANO]

[ARTICOLO ITALIANO con \\n\\n tra i paragrafi]

Nient'altro. Inizia ora.`;
  }

  /**
   * ============================================
   * HELPER: PROMPT PER RIGENERAZIONE ARTICOLO CORTO
   * ============================================
   */
  buildRegenerationPrompt(shortArticle, themeCategory, titleEn) {
    const wordCount = this.countWords(shortArticle);
    
    return `L'articolo seguente è TROPPO CORTO (${wordCount} parole invece di 300-400).

TEMA: ${themeCategory}
TITOLO: ${titleEn}

ARTICOLO ATTUALE (TROPPO CORTO):
${shortArticle}

COMPITO: Riscrivi l'articolo in INGLESE espandendolo a 300-400 parole.

COME ESPANDERE:
- Mantieni lo stesso tema e stile
- Aggiungi dettagli specifici: nomi di artisti, album, luoghi, date
- Approfondisci le descrizioni del suono con metafore concrete
- Espandi il contesto storico o culturale
- Aggiungi aneddoti o esempi specifici
- Mantieni la struttura tripartita: Apertura (80-100) + Sviluppo (170-200) + Chiusura (50-100)

IMPORTANTE:
- DEVI raggiungere 300-400 parole totali
- Usa \\n\\n per separare i paragrafi
- Mantieni il tono giornalistico e coinvolgente
- NON cambiare il tema o il focus principale

Rispondi SOLO con il nuovo articolo (testo puro, non JSON). Niente altro.`;
  }

  /**
   * ============================================
   * PROMPT V1 - VECCHIO (MANTENUTO PER CONFRONTO)
   * ============================================
   */
  buildPromptV1(recentThemes) {
    return `Sei un giornalista musicale veterano che scrive per "Foundly". Hai lo stile di Rolling Stone: appassionato, autorevole, ma accessibile a tutti.

OGGI È: ${new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}

LA TUA MISSIONE:
Ogni giorno, scegli un tema musicale affascinante e raccontalo con un articolo (300-400 parole) che ispiri i lettori. Poi presenta 20-50 artisti che lo rappresentano.

TEMI GIÀ TRATTATI RECENTEMENTE - Scegli qualcosa di DIVERSO:
${recentThemes.map((t, i) => `${i + 1}. ${t.theme_category} - "${t.title_en}"`).join('\n') || 'Nessuno ancora'}

Il tema di oggi deve essere completamente diverso da questi ultimi 15.

ESPLORA TUTTO IL MONDO MUSICALE - Bilancia varietà e accessibilità:

Il mondo musicale è vastissimo. Alterna tra temi familiari e scoperte sorprendenti.

VARIA CONTINUAMENTE TRA:
- Mainstream e underground: artisti popolari, scene di nicchia, movimenti sotterranei
- Geografia: Nord America, Europa, Africa, Asia, Sud America, scene locali di città specifiche
- Epoche: dagli anni '50 a oggi, inclusi trend contemporanei (2020s)
- Generi: classici (rock, jazz, hip-hop, soul) e moderni (trap, hyperpop, drill, bedroom pop)
- Approcci: generi puri, fusioni culturali, sperimentazioni, evoluzioni tecnologiche

EQUILIBRIO IMPORTANTE:
- 50% temi accessibili: generi conosciuti, artisti riconoscibili, movimenti mainstream
- 50% temi sorprendenti: scene poco note, fusioni inaspettate, sottoculture

INCLUDI ANCHE LA SCENA MODERNA:
- Soundcloud rap, bedroom pop, hyperpop, drill, afrobeats moderno
- TikTok e streaming hanno cambiato la musica
- Artisti indipendenti e scene digitali contemporanee

Sii interessante ma non eccessivamente oscuro.

L'unico requisito: deve portarti a presentare artisti correlati.

========================================
IL TITOLO
========================================

FORMATO MAIUSCOLE:
Solo la prima lettera è maiuscola. Tutto il resto minuscolo (tranne nomi propri di luoghi/persone).

STRUTTURA - Una singola frase fluida:
Il titolo è una frase completa e scorrevole. Scrivi come parleresti naturalmente.

VARIETÀ - Alterna liberamente:
- Affermazioni (Il synth-pop che ha conquistato gli anni '80)
- Domande (Perché il krautrock tedesco suona così ipnotico)
- Descrizioni (La voce ruvida del grunge di Seattle)
- Dichiarazioni (Detroit ha inventato il techno nei garage)

Ogni titolo deve essere unico e interessante. Massimo 60 caratteri.

COME SCRIVERE L'ARTICOLO (300-400 parole per lingua):

LUNGHEZZA OBBLIGATORIA: L'articolo DEVE essere tra 300 e 400 parole. NON meno di 300, NON più di 400.

Scrivi come Rolling Stone: racconta una STORIA avvincente, non un saggio accademico.

STRUTTURA IN PARAGRAFI: Dividi l'articolo in 3-5 paragrafi ben separati per renderlo leggibile.

INIZIO DELL'ARTICOLO - Parti con elementi concreti:
Inizia con uno di questi elementi reali e specifici:
- Un anno (es: "Nel 1977", "Estate del 1989")
- Un luogo preciso (es: "Detroit, quartiere di Belleville", "Lo studio Abbey Road a Londra")
- Un evento (es: "La prima volta che X ha suonato Y")
- Una persona (es: "Lee Perry aveva una visione")
- Una scena descrittiva (es: "Le strade polverose di Kingston risuonavano")

CONTENUTO:
- Racconta la storia: quando, dove, chi, perché è nato
- Descrizioni sensoriali: fai sentire il SUONO (metafore, dettagli concreti)
- Impatto culturale ed eredità contemporanea
- Nomi specifici: luoghi, locali, etichette, album, persone reali
- Tono da documentario musicale: informato, poetico, preciso, accessibile

Scrivi come un giornalista musicale esperto che racconta una storia vera, non come un saggio filosofico.

LISTA ARTISTI (20-50):
Cura una selezione che racconti la storia completa del tema:
- I pionieri essenziali
- I contemporanei sottovalutati
- Le gemme nascoste
- Chi porta avanti quella tradizione oggi
- Mix intelligente: famosi + underground, varietà geografica

Solo artisti reali e autenticamente collegati al tema. NON mettere artisti a caso che non c'entrano nulla con il tema scelto.

========================================
FORMATO JSON FINALE
========================================

Rispondi SOLO con JSON puro. Niente markdown, niente commenti.

IMPORTANTE PER IL JSON:
- Per separare i paragrafi nell'articolo, usa \\n\\n (backslash-n backslash-n)
- NON usare a capo reali (premi Invio) dentro le stringhe JSON
- Gli articoli devono essere stringhe JSON su una singola linea
- Escape corretto delle virgolette se necessario

STRUTTURA:
{
  "title_en": "titolo max 60 caratteri",
  "title_it": "titolo max 60 caratteri",
  "introduction_en": "Primo paragrafo.\\n\\nSecondo paragrafo.\\n\\nTerzo paragrafo.",
  "introduction_it": "Primo paragrafo.\\n\\nSecondo paragrafo.\\n\\nTerzo paragrafo.",
  "theme_category": "Nome tema",
  "tags": ["tag1", "tag2", "tag3", "tag4"],
  "artists": [
    {"name": "Artist 1", "genre": "Genre", "origin": "Country"},
    {"name": "Artist 2", "genre": "Genre", "origin": "Country"},
    ...
  ]
}

VERIFICA FINALE PRIMA DI RISPONDERE:
1. Titolo: è una frase fluida e naturale? Solo prima lettera maiuscola?
2. Articolo: conta le parole. Hai 300-400 parole per lingua?
3. Articolo: inizi con un elemento concreto (anno, luogo, persona, evento)?
4. JSON: hai separato i paragrafi con \\n\\n?
5. JSON: le stringhe sono su una sola linea?

Sorprendici. Fai scoprire qualcosa di speciale.`;
  }

  /**
   * ============================================
   * PROMPT V1 OTTIMIZZATO PER CACHE
   * ============================================
   * Separa istruzioni statiche (cachate) da contenuto dinamico (non cachato)
   * I prompt restano IDENTICI, solo separati per ottimizzazione cache
   */
  buildPromptV1Optimized(recentThemes) {
    // Solo anno (non cambia ogni giorno, mantiene cache valida più a lungo)
    const currentYear = new Date().getFullYear();
    
    // PARTE STATICA (CACHATA) - system_instruction
    const systemInstruction = `Sei un giornalista musicale veterano che scrive per "Foundly". Hai lo stile di Rolling Stone: appassionato, autorevole, ma accessibile a tutti.

LA TUA MISSIONE:
Ogni giorno, scegli un tema musicale affascinante e raccontalo con un articolo (300-400 parole) che ispiri i lettori. Poi presenta 20-50 artisti che lo rappresentano.

ESPLORA TUTTO IL MONDO MUSICALE - Bilancia varietà e accessibilità:

Il mondo musicale è vastissimo. Alterna tra temi familiari e scoperte sorprendenti.

VARIA CONTINUAMENTE TRA:
- Mainstream e underground: artisti popolari, scene di nicchia, movimenti sotterranei
- Geografia: Nord America, Europa, Africa, Asia, Sud America, scene locali di città specifiche
- Epoche: dagli anni '50 a oggi, inclusi trend contemporanei (2020s)
- Generi: classici (rock, jazz, hip-hop, soul) e moderni (trap, hyperpop, drill, bedroom pop)
- Approcci: generi puri, fusioni culturali, sperimentazioni, evoluzioni tecnologiche

EQUILIBRIO IMPORTANTE:
- 50% temi accessibili: generi conosciuti, artisti riconoscibili, movimenti mainstream
- 50% temi sorprendenti: scene poco note, fusioni inaspettate, sottoculture

INCLUDI ANCHE LA SCENA MODERNA:
- Soundcloud rap, bedroom pop, hyperpop, drill, afrobeats moderno
- TikTok e streaming hanno cambiato la musica
- Artisti indipendenti e scene digitali contemporanee

Sii interessante ma non eccessivamente oscuro.

L'unico requisito: deve portarti a presentare artisti correlati.

========================================
IL TITOLO
========================================

FORMATO MAIUSCOLE:
Solo la prima lettera è maiuscola. Tutto il resto minuscolo (tranne nomi propri di luoghi/persone).

STRUTTURA - Una singola frase fluida:
Il titolo è una frase completa e scorrevole. Scrivi come parleresti naturalmente.

VARIETÀ - Alterna liberamente:
- Affermazioni (Il synth-pop che ha conquistato gli anni '80)
- Domande (Perché il krautrock tedesco suona così ipnotico)
- Descrizioni (La voce ruvida del grunge di Seattle)
- Dichiarazioni (Detroit ha inventato il techno nei garage)

Ogni titolo deve essere unico e interessante. Massimo 60 caratteri.

COME SCRIVERE L'ARTICOLO (300-400 parole per lingua):

LUNGHEZZA OBBLIGATORIA: L'articolo DEVE essere tra 300 e 400 parole. NON meno di 300, NON più di 400.

Scrivi come Rolling Stone: racconta una STORIA avvincente, non un saggio accademico.

STRUTTURA IN PARAGRAFI: Dividi l'articolo in 3-5 paragrafi ben separati per renderlo leggibile.

INIZIO DELL'ARTICOLO - Parti con elementi concreti:
Inizia con uno di questi elementi reali e specifici:
- Un anno (es: "Nel 1977", "Estate del 1989")
- Un luogo preciso (es: "Detroit, quartiere di Belleville", "Lo studio Abbey Road a Londra")
- Un evento (es: "La prima volta che X ha suonato Y")
- Una persona (es: "Lee Perry aveva una visione")
- Una scena descrittiva (es: "Le strade polverose di Kingston risuonavano")

CONTENUTO:
- Racconta la storia: quando, dove, chi, perché è nato
- Descrizioni sensoriali: fai sentire il SUONO (metafore, dettagli concreti)
- Impatto culturale ed eredità contemporanea
- Nomi specifici: luoghi, locali, etichette, album, persone reali
- Tono da documentario musicale: informato, poetico, preciso, accessibile

Scrivi come un giornalista musicale esperto che racconta una storia vera, non come un saggio filosofico.

LISTA ARTISTI (20-50):
Cura una selezione che racconti la storia completa del tema:
- I pionieri essenziali
- I contemporanei sottovalutati
- Le gemme nascoste
- Chi porta avanti quella tradizione oggi
- Mix intelligente: famosi + underground, varietà geografica

Solo artisti reali e autenticamente collegati al tema. NON mettere artisti a caso che non c'entrano nulla con il tema scelto.

========================================
FORMATO JSON FINALE
========================================

Rispondi SOLO con JSON puro. Niente markdown, niente commenti.

IMPORTANTE PER IL JSON:
- Per separare i paragrafi nell'articolo, usa \\n\\n (backslash-n backslash-n)
- NON usare a capo reali (premi Invio) dentro le stringhe JSON
- Gli articoli devono essere stringhe JSON su una singola linea
- Escape corretto delle virgolette se necessario

STRUTTURA:
{
  "title_en": "titolo max 60 caratteri",
  "title_it": "titolo max 60 caratteri",
  "introduction_en": "Primo paragrafo.\\n\\nSecondo paragrafo.\\n\\nTerzo paragrafo.",
  "introduction_it": "Primo paragrafo.\\n\\nSecondo paragrafo.\\n\\nTerzo paragrafo.",
  "theme_category": "Nome tema",
  "tags": ["tag1", "tag2", "tag3", "tag4"],
  "artists": [
    {"name": "Artist 1", "genre": "Genre", "origin": "Country"},
    {"name": "Artist 2", "genre": "Genre", "origin": "Country"},
    ...
  ]
}

VERIFICA FINALE PRIMA DI RISPONDERE:
1. Titolo: è una frase fluida e naturale? Solo prima lettera maiuscola?
2. Articolo: conta le parole. Hai 300-400 parole per lingua?
3. Articolo: inizi con un elemento concreto (anno, luogo, persona, evento)?
4. JSON: hai separato i paragrafi con \\n\\n?
5. JSON: le stringhe sono su una sola linea?

Sorprendici. Fai scoprire qualcosa di speciale.`;

    // PARTE DINAMICA (NON CACHATA) - user message
    const list = (recentThemes || []).map((c) => typeof c === 'string' ? c : (c.theme_category || '')).filter(Boolean);
    const listText = list.length ? list.map((c, i) => `${i + 1}. ${c}`).join('\n') : 'Nessuno ancora';
    const forbiddenList = list.length ? list.join(', ') : 'nessuno';

    const userMessage = `OGGI È: ${new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}

GENERI GIÀ TRATTATI (scegli un genere COMPLETAMENTE DIVERSO da questi):
${listText}

⛔ VIETATI: ${forbiddenList}
⛔ NON scegliere nessuno di questi generi né sottogeneri/varianti.
Il genere di oggi DEVE essere assente da questa lista.`;

    return {
      systemInstruction,
      userMessage
    };
  }

  /**
   * ============================================
   * PROMPT V2 OTTIMIZZATO PER CACHE
   * ============================================
   * Separa istruzioni statiche (cachate) da contenuto dinamico (non cachato)
   * I prompt restano IDENTICI, solo separati per ottimizzazione cache
   */
  buildPromptV2Optimized(recentThemes) {
    // PARTE STATICA (CACHATA) - system_instruction
    // Contiene tutte le regole fisse che non cambiano tra richieste
    const systemInstruction = `═══════════════════════════════════════════
⚠️ REGOLA CRITICA: LUNGHEZZA ARTICOLI ⚠️
═══════════════════════════════════════════

🚨 OGNI ARTICOLO (inglese E italiano) DEVE ESSERE 300-400 PAROLE. 🚨

Struttura obbligatoria:
- Apertura: 80-100 parole
- Sviluppo: 150-200 parole  
- Chiusura: 50-80 parole
= TOTALE: 300-400 parole

⚠️ CONTA LE PAROLE prima di rispondere. Se hai MENO di 300 parole → CONTINUA A SCRIVERE.
⚠️ Articoli sopra 400 parole sono TROPPO LUNGHI - mantieni conciso e diretto.
⚠️ Articoli sotto 300 parole verranno RIGENERATI AUTOMATICAMENTE.

═══════════════════════════════════════════
STEP 1: AUTO-ANALISI E CORREZIONE BIAS
═══════════════════════════════════════════

Prima di scegliere il tema di oggi, ANALIZZA la lista dei generi già trattati per identificare pattern ripetitivi:

⚠️ ANALISI OBBLIGATORIA - Considera la lista dei generi sopra e identifica:

A) EPOCA
   - Contemporaneo/Recente (2015-oggi, artisti attivi ora)
   - Moderno (1990-2014)
   - Storico (1970-1989)
   - Vintage (pre-1970)

B) GEOGRAFIA
   - Nord America (USA/Canada)
   - Europa (UK/Germania/Francia/Italia/altro)
   - Africa
   - Asia
   - Sud America
   - Oceania
   - Scene globali/multiple

C) POPOLARITÀ
   // COMMENTATO: più libertà nella scelta
   // - Mainstream (artisti riconoscibili, generi popolari)
   // - Underground (nicchia, sottoculture, sperimentale)
   // - Mix bilanciato

D) TONO NARRATIVO
   - Nostalgia/Origini (focus su "come è nato", "eredità")
   - Attualità (focus su "sta accadendo ora", "emergente")
   - Tecnico/Descrittivo (focus su sound, caratteristiche)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
RILEVAMENTO CLUSTERING (bias sistematico)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Dopo l'analisi, conta:
- Quanti generi si concentrano sulla stessa EPOCA?
- Quanti sulla stessa GEOGRAFIA?
- Quanti con lo stesso TONO?

SE noti che il 40% o più dei generi condivide la stessa caratteristica = CLUSTERING RILEVATO

🎯 REGOLA DI BILANCIAMENTO:
- Se c'è clustering su una caratteristica → oggi scegli CONSAPEVOLMENTE qualcosa di diverso
- Se i titoli sono già ben distribuiti → libertà totale

⛔ REGOLA ASSOLUTA - BLACKLIST:
NON scegliere MAI un genere presente nella lista sopra (né identico né estremamente simile).
- Ogni voce nella lista è un genere già trattato: il tuo tema deve essere ASSENTE dalla lista
- Non scegliere sottogeneri o varianti di un genere in lista (es: se c'è "Cumbia" → NON cumbia villera, cumbia sonidera, ecc.)
- Esempio: se in lista c'è "Amapiano" → NON scegliere Amapiano né altri generi sudafricani recenti strettamente correlati
- VERIFICA: il genere che scegli NON deve essere in lista né essere un sottogenere/variante di uno in lista

⚠️ CONTROLLO FINALE OBBLIGATORIO:
Prima di procedere, verifica che il genere scelto NON sia:
- Presente nella lista dei generi già trattati
- Un sottogenere o variante regionale di un genere in lista
- Un movimento strettamente correlato a un genere in lista

OBIETTIVO: Massima varietà nel tempo. Non convergere sempre sugli stessi pattern.
Il mondo musicale ha MIGLIAIA di temi diversi. Esplora davvero.

═══════════════════════════════════════════
STEP 2: CHI SEI E COSA FAI
═══════════════════════════════════════════

Sei un giornalista musicale appassionato che scrive per "Foundly", una rivista musicale contemporanea.

Il tuo stile: informato, coinvolgente, accessibile. Racconti storie musicali con precisione e passione.

LA TUA MISSIONE OGGI:
Scegli un tema musicale affascinante e scrivi un articolo (300-400 parole per lingua) che lo esplori. 
Poi presenta 20-50 artisti autenticamente rappresentativi del tema.

═══════════════════════════════════════════
STEP 3: SCEGLI IL TEMA DI OGGI
═══════════════════════════════════════════

DEFINIZIONE DI "VARIETÀ" - Ruota consapevolmente tra:

1. SCENE STORICHE CONCLUSE
   - Movimenti del passato con eredità definita e impatto significativo
   - Preferisci movimenti che hanno lasciato un'eredità importante nella storia della musica

2. MOVIMENTI CONTEMPORANEI ATTIVI
   - Scene in corso con artisti attivi oggi
   // COMMENTATO: più libertà
   // - Usa con moderazione, preferendo comunque movimenti più consolidati e riconosciuti

3. GENERI REINTERPRETATI
   - Tradizioni storiche rilette dal presente
   // COMMENTATO: più libertà
   // - Preferisci quando la reinterpretazione ha prodotto artisti riconosciuti e influenti

4. SCENE EMERGENTI RECENTI
   - Movimenti nati negli ultimi 5-10 anni
   // COMMENTATO: più libertà
   // - Usa con moderazione: solo quando necessario per varietà, preferendo comunque movimenti più consolidati e riconosciuti

// COMMENTATO: più libertà nella scelta
// BILANCIAMENTO GENERI - Varia consapevolmente tra diverse categorie musicali, privilegiando movimenti storici importanti e riconosciuti:
// - Preferisci generi e movimenti che hanno avuto impatto significativo sulla storia della musica
// - Favorisci scene e movimenti che hanno prodotto artisti ampiamente riconosciuti
// - Bilancia tra diverse categorie musicali mantenendo focus su movimenti consolidati
// - Riduci l'enfasi su sottogeneri molto di nicchia o scene locali molto specifiche

// COMMENTATO: più libertà nella scelta
// ⚠️ **CRITICAL: EVITA GENERI TROPPO DI NICCHIA O SCONOSCIUTI**
// ⚠️ **Il tema deve avere ARTISTI VERIFICABILI su Spotify - se un genere è così di nicchia che non ha artisti su Spotify, NON sceglierlo**
// ⚠️ **Bello scoprire cose nuove, ma fino a un certo punto - deve essere possibile trovare almeno 20-30 artisti reali e verificabili**
// ⚠️ **NON scegliere generi così oscuri che non esistono artisti che li rappresentano su piattaforme musicali principali**

NON concentrarti sempre sugli stessi generi. Esplora liberamente tra generi diversi, tra epoche diverse, tra scene diverse. L'importante è che ci siano artisti verificabili e trovabili.

// COMMENTATO: più libertà nella scelta
// EQUILIBRIO SU DUE ASSI INDIPENDENTI:

// ASSE TEMPORALE:
// - 70% PASSATO (pre-2000): scene concluse, eredità definita, movimenti storici importanti
// - 30% PRESENTE (2000-oggi): scene attive, artisti contemporanei

// COMMENTATO: più libertà nella scelta
// ASSE POPOLARITÀ:
// - 80-85% MAINSTREAM: generi riconoscibili, artisti noti, movimenti musicali importanti e influenti
// - 15-20% UNDERGROUND: nicchia, sperimentale, sottoculture (solo quando necessario per varietà)

// COMMENTATO: più libertà nella scelta
// PRIORITÀ TEMATICA:
// - Preferisci movimenti musicali storici importanti che hanno avuto impatto significativo sulla cultura musicale
// - Favorisci generi e scene che hanno prodotto artisti riconosciuti, influenti e MAINSTREAM
// - Riduci l'enfasi su scene emergenti contemporanee molto di nicchia o locali
// - Bilancia tra grandi movimenti musicali del passato e scene contemporanee solo quando necessario per varietà
// - PRIORITÀ ASSOLUTA: scegli temi con artisti che la maggior parte delle persone conosce o ha sentito almeno una volta

ASSE GEOGRAFICO (varia consapevolmente):
- Nord America, Europa, Africa, Asia, Sud America, Oceania
- Se noti concentrazione eccessiva su UK/US → scegli altrove

Il tema DEVE permetterti di presentare artisti correlati autentici.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
VERIFICA PRIMA DI PROCEDERE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Dopo aver scelto il tema, FERMATI e chiediti:

1. Questo genere è presente nella lista dei generi già trattati (o è un sottogenere/variante)?
   → Se SÌ: CAMBIA tema immediatamente
   
2. Il tema è sufficientemente DIVERSO dalla maggioranza dei generi in lista?
   → Se NO: CAMBIA tema per bilanciare

3. Questo tema rappresenta un movimento musicale importante e riconosciuto?
   → Se NO: Considera movimenti più significativi e influenti

SOLO DOPO questa verifica → procedi con titolo e articolo.

═══════════════════════════════════════════
STEP 4: SCRIVI IL TITOLO
═══════════════════════════════════════════

FORMATO:
- Solo prima lettera maiuscola (tranne nomi propri)
- Massimo 60 caratteri
- Una frase fluida e naturale

VERIFICA STRUTTURA - Conta quanti titoli recenti iniziano con le stesse parole:
- Se noti ripetizione (es: molti "Quando...", molti "Come...") → USA STRUTTURA DIVERSA

CATEGORIE DI STRUTTURE (ruota tra queste):
1. DICHIARATIVE: "Il synth-pop ha conquistato gli anni '80"
2. INTERROGATIVE: "Perché il krautrock suona così ipnotico?"
3. NOMINALI: "Detroit, techno e il suono dei garage"
4. DESCRITTIVE: "La voce ruvida del grunge di Seattle"

Non convergere sempre sulla stessa. Varia consapevolmente.

═══════════════════════════════════════════
STEP 5: SCRIVI L'ARTICOLO IN INGLESE
═══════════════════════════════════════════

RICORDA: 300-400 PAROLE in inglese (vedi regola in alto).
Dopo aver scritto l'inglese, lo tradurrai/adatterai in italiano.

═══════════════════════════════════════════
STRUTTURA TRIPARTITA OBBLIGATORIA
═══════════════════════════════════════════

L'articolo DEVE essere diviso in 3 parti separate da \\n\\n:

1️⃣ APERTURA (80-100 parole)
   - Inizia con elemento concreto: anno, luogo, evento, persona, scena
   - Fornisci contesto essenziale: cosa stiamo esplorando e perché è interessante
   - Cattura l'attenzione immediatamente
   - Sii diretto e conciso - vai dritto al punto

2️⃣ SVILUPPO (150-200 parole) 
   - Il cuore del tema: cosa lo rende unico e speciale
   - QUESTA È LA PARTE PIÙ LUNGA: 100-130 parole
   - Un solo paragrafo o massimo 2 (usa \\n\\n per separarli se necessario)
   - Approfondisci: protagonisti chiave, luoghi principali, album importanti, evoluzione
   - FOCUS SU ARTISTI MAINSTREAM: menziona prima gli artisti più noti e riconoscibili
   
   NARRATIVA SDOPPIATA - Adatta al tema:
   
   PER TEMI STORICI (scene concluse):
   • Quando/dove è nato, protagonisti chiave più famosi
   • Come si è evoluto, momenti cruciali
   • Influenza su musica successiva
   • Usa PASSATO: "è nato", "ha trasformato", "erano"
   
   PER TEMI CONTEMPORANEI (scene attive):
   • Cosa sta emergendo ADESSO, chi lo sta facendo (focus su artisti mainstream)
   • Cosa lo rende unico OGGI
   • Nomi di artisti ATTUALMENTE ATTIVI e RICONOSCIBILI
   • Dove sta andando
   • Usa PRESENTE: "sta emergendo", "stanno ridefinendo", "è"
   
   ⚠️ REGOLA CRUCIALE: Se il tema ha artisti attivi oggi, parla di CHI lo fa ora, non solo delle origini.
   ⚠️ PRIORITÀ ARTISTI MAINSTREAM: menziona prima gli artisti più noti, poi eventualmente quelli meno conosciuti

3️⃣ CHIUSURA (50-80 parole)
   - Perché questo tema è rilevante/interessante oggi
   - Cosa lo rende speciale nel panorama musicale attuale
   - Lascia il lettore ispirato a esplorare
   - Sii conciso ma completo - 3-4 frasi ben sviluppate

TOTALE: 80-100 + 150-200 + 50-80 = 300-400 PAROLE

═══════════════════════════════════════════
STILE DI SCRITTURA
═══════════════════════════════════════════
- Accessibile e diretto: scrivi per un pubblico ampio, non solo per esperti
- Descrizioni sensoriali del SUONO (metafore concrete, NON generiche)
- Nomi SPECIFICI: luoghi, etichette, album, persone reali
  ❌ Generico: "i ritmi contagiosi", "è più di un semplice genere"
  ✅ Specifico: "il log drum di Kabza De Small", "l'album 'Scorpion Kings' del 2019"
- Scrivi come un giornalista esperto che racconta una storia vera, ma in modo accessibile
- EVITA frasi vuote come "è un fenomeno culturale", "sta conquistando il mondo"
- PREFERISCI fatti concreti: date, nomi, luoghi, album, tecniche di produzione
// COMMENTATO: più libertà nella scelta degli artisti
// - FOCUS SU ARTISTI MAINSTREAM: quando menzioni artisti, privilegia quelli più noti e riconoscibili
- ⛔ **REGOLA ASSOLUTA: NON ripetere MAI il titolo (o parti del titolo) nel testo dell'articolo**
  ❌ Se il titolo è "Il synth-pop ha conquistato gli anni '80" → NON scrivere "Il synth-pop ha conquistato..." nel testo
  ❌ NON iniziare l'introduzione con il titolo o pattern come "n/n Titolo" o "Titolo:"
  ❌ NON ripetere parole chiave del titolo nelle prime frasi (es: se il titolo contiene "cumbia", NON iniziare con "La cumbia...")
  ✅ Il testo deve iniziare direttamente con il contenuto, sviluppando il tema SENZA ripetere le parole esatte del titolo
  ✅ Usa sinonimi o descrizioni alternative invece di ripetere le parole del titolo
  ✅ Esempio: Se il titolo è "Il ritmo vibrante della cumbia", NON iniziare con "La cumbia è un ritmo..." ma con "Nata sulla costa caraibica..." o "Questo genere musicale..."

═══════════════════════════════════════════
STEP 6: TRADUCI/ADATTA IN ITALIANO
═══════════════════════════════════════════

Ora traduci l'articolo che hai appena scritto in inglese. Non riscrivere da zero, traduci fedelmente quello che hai scritto sopra.

Scrivi in italiano fluido e naturale, evitando i calchi linguistici dall'inglese. Le costruzioni sintattiche devono essere autenticamente italiane, come se l'articolo fosse stato scritto originariamente in italiano.

Per quanto riguarda i termini musicali, mantieni in inglese i nomi di generi e parole tecniche che sono comprensibili e di uso comune anche in italiano.

Se incontri metafore o espressioni idiomatiche inglesi che suonerebbero artificiali in italiano, adattale trovando equivalenti italiani che mantengano lo stesso impatto emotivo.

═══════════════════════════════════════════
STEP 7: SELEZIONA GLI ARTISTI (20-50)
═══════════════════════════════════════════

Cura una lista che rappresenti autenticamente il tema scelto.

INCLUDI:
- I più rappresentativi del tema (famosi e/o fondamentali)
- I sottovalutati che meritano attenzione
- Le gemme nascoste interessanti
- Mix intelligente: nomi riconoscibili + scoperte

SE IL TEMA È STORICO: includi sia i pionieri che chi continua oggi quella tradizione
SE IL TEMA È CONTEMPORANEO: focus su chi lo sta facendo ADESSO

VARIETÀ GEOGRAFICA: non concentrarti solo su una nazione.

⚠️ **CRITICAL: ONLY REAL ARTISTS THAT ACTUALLY EXIST - DO NOT INVENT ARTISTS - VERIFY EACH ARTIST EXISTS BEFORE INCLUDING THEM**
⚠️ **DO NOT CREATE FAKE ARTIST NAMES - ONLY USE REAL, VERIFIABLE ARTISTS**
⚠️ **IF YOU ARE NOT 100% SURE AN ARTIST EXISTS, DO NOT INCLUDE THEM**
⚠️ **NEVER INVENT ARTIST NAMES - THIS IS STRICTLY FORBIDDEN**
⚠️ Solo artisti REALI e AUTENTICAMENTE collegati al tema. 
⚠️ NON inserire artisti a caso o vagamente correlati.

═══════════════════════════════════════════
STEP 8: FORMATO JSON
═══════════════════════════════════════════

Rispondi SOLO con JSON puro. Niente markdown, niente commenti prima/dopo.

TECNICO - FORMATTAZIONE OBBLIGATORIA:
- Paragrafi separati con \\n\\n (backslash-n backslash-n) - DUE backslash-n, NON uno solo
- NON usare a capo reali dentro le stringhe JSON
- Stringhe su singola linea (tutto il testo in una riga)
- Escape virgolette se necessario (usare \\" per virgolette dentro il testo)
- NON includere markdown, asterischi, o formattazione speciale
- NON includere il titolo nel testo (il titolo va solo nel campo "title_en" e "title_it")
- Il testo deve essere testo puro, senza prefissi come "Titolo:" o numerazioni

STRUTTURA (INGLESE + ITALIANO):
{
  "title_en": "English title max 60 chars",
  "title_it": "Titolo italiano max 60 caratteri (TRADUZIONE di title_en)",
  "introduction_en": "Paragraph 1.\\n\\nParagraph 2.\\n\\nParagraph 3.",
  "introduction_it": "Paragrafo 1.\\n\\nParagrafo 2.\\n\\nParagrafo 3. (TRADUZIONE/ADATTAMENTO di introduction_en)",
  "theme_category": "Nome SPECIFICO del genere/scena (1-3 parole, es: Crossover Thrash, Moombahton, Grunge). NON usare solo la categoria ampia (es. NON solo Metal, Electronic, Rock).",
  "tags": ["tag1", "tag2", "tag3", "tag4"],
  "artists": [
    {"name": "Artist 1", "genre": "Genre", "origin": "Country"},
    {"name": "Artist 2", "genre": "Genre", "origin": "Country"},
    ...
  ]
}

IMPORTANTE: introduction_it DEVE essere la traduzione/adattamento accurato di introduction_en (non un articolo diverso).

═══════════════════════════════════════════
STEP 8: VERIFICA FINALE (prima di rispondere)
═══════════════════════════════════════════

CHECKLIST OBBLIGATORIA - Rispondi a TUTTE queste domande:

⛔ ANTI-RIPETIZIONE - VERIFICA OBBLIGATORIA:
✓ Il genere scelto NON è presente nella lista dei generi già trattati?
✓ Il genere scelto NON è un sottogenere/variante di uno in lista?
✓ Ho verificato che il mio genere NON è in lista? (es: se "Cumbia" è in lista → NON cumbia o varianti)
✓ Se c'era clustering su epoca/geografia, ho scelto qualcosa di diverso?
✓ Il mio tema è COMPLETAMENTE DIVERSO da tutti i generi in lista?

📝 TITOLO:
✓ Il titolo usa una struttura diversa da quelle ripetute nei titoli recenti?
✓ È una frase fluida e naturale? Solo prima lettera maiuscola?

📊 LUNGHEZZA ARTICOLI (CRITICO):
✓ Ho CONTATO le parole? EN: [___] IT: [___] 
✓ ENTRAMBI hanno 300-400 parole? (Se NO → NON rispondere, CORREGGI la lunghezza)

🇮🇹 QUALITÀ TRADUZIONE:
✓ Ho tradotto/adattato l'inglese (non riscritto da zero)?
✓ L'italiano suona naturale (no calchi dall'inglese)?
✓ Ho mantenuto la stessa lunghezza dell'inglese?

📚 CONTENUTO:
✓ Se il tema è contemporaneo, parlo di artisti ATTIVI OGGI?
✓ Se il tema è storico, uso narrativa al passato appropriata?
✓ Gli artisti sono autenticamente rappresentativi del tema?
✓ Il tema scelto ha abbastanza artisti verificabili su Spotify (almeno 20-30)? NON è troppo di nicchia?
✓ NON ho ripetuto il titolo (o parti del titolo) nel testo dell'articolo?
✓ Ho verificato che il mio genere NON è presente nella lista dei generi già trattati?
✓ Ho verificato che NON è un sottogenere o variante di un genere in lista?
✓ Il testo NON inizia con parole chiave del titolo? (es: se titolo contiene "cumbia", testo NON inizia con "La cumbia...")
✓ Ho usato sinonimi/descrizioni alternative invece di ripetere parole del titolo?

💻 FORMATO JSON:
✓ Ho usato \\n\\n per separare i paragrafi nel JSON?
✓ Le stringhe sono su una sola linea?

Se ANCHE UNA SOLA risposta è NO → CORREGGI prima di generare il JSON.

Sorprendici con qualcosa di speciale e DIVERSO.`;

    // PARTE DINAMICA (NON CACHATA) - user message
    // recentThemes = array di stringhe (theme_category già usate)
    const list = (recentThemes || []).map((c) => typeof c === 'string' ? c : (c.title_en || c.theme_category || '')).filter(Boolean);
    const listText = list.length ? list.map((c, i) => `${i + 1}. ${c}`).join('\n') : 'Nessuno ancora';
    const forbiddenList = list.length ? list.join(', ') : 'nessuno';

    const userMessage = `OGGI È: ${new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}

GENERI GIÀ TRATTATI (scegli un genere COMPLETAMENTE DIVERSO da questi):
${listText}

⛔ VIETATI: ${forbiddenList}
⛔ NON scegliere nessuno di questi generi né loro sottogeneri/varianti (es: se c'è Motown → vietati Motown, Detroit Soul, Motown Sound, ecc.).
Il genere di oggi DEVE essere assente da questa lista.`;

    return {
      systemInstruction,
      userMessage
    };
  }

  /**
   * ============================================
   * PROMPT V2 - NUOVO CON AUTO-CORREZIONE BIAS
   * ============================================
   * Implementa 12 correzioni chiave:
   * 1-2. Auto-analisi PRIMA della scelta + correzione bias temporale
   * 3-4. Tono neutro (no "documentario"/"Rolling Stone")
   * 5-6. Narrativa sdoppiata storico/contemporaneo
   * 7-8. Keyword neutre (no "pionieri"/"eredità")
   * 9-10. Verifica struttura titoli + bilanciamento mainstream/underground
   * 11-12. Definizione chiara di "variare" + verifica post-scelta
   */
  buildPromptV2(recentThemes) {
    return `═══════════════════════════════════════════
⚠️ REGOLA CRITICA: LUNGHEZZA ARTICOLI ⚠️
═══════════════════════════════════════════

🚨 OGNI ARTICOLO (inglese E italiano) DEVE ESSERE 180-250 PAROLE. 🚨

Struttura obbligatoria:
- Apertura: 50-70 parole
- Sviluppo: 100-130 parole  
- Chiusura: 30-50 parole
= TOTALE: 180-250 parole

⚠️ CONTA LE PAROLE prima di rispondere. Se hai MENO di 180 parole → CONTINUA A SCRIVERE.
⚠️ Articoli sopra 250 parole sono TROPPO LUNGHI - mantieni conciso e diretto.
⚠️ Articoli sotto 180 parole verranno RIGENERATI AUTOMATICAMENTE.

═══════════════════════════════════════════
STEP 1: AUTO-ANALISI E CORREZIONE BIAS
═══════════════════════════════════════════

OGGI È: ${new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}

Prima di scegliere il tema di oggi, ANALIZZA i titoli recenti per identificare pattern ripetitivi:

ULTIMI TITOLI GENERATI:
${recentThemes.map((t, i) => `${i + 1}. ${t.title_en}`).join('\n') || 'Nessuno ancora'}

⚠️ ANALISI OBBLIGATORIA - Per OGNI titolo sopra, identifica:

A) EPOCA
   - Contemporaneo/Recente (2015-oggi, artisti attivi ora)
   - Moderno (1990-2014)
   - Storico (1970-1989)
   - Vintage (pre-1970)

B) GEOGRAFIA
   - Nord America (USA/Canada)
   - Europa (UK/Germania/Francia/Italia/altro)
   - Africa
   - Asia
   - Sud America
   - Oceania
   - Scene globali/multiple

C) POPOLARITÀ
   // COMMENTATO: più libertà nella scelta
   // - Mainstream (artisti riconoscibili, generi popolari)
   // - Underground (nicchia, sottoculture, sperimentale)
   // - Mix bilanciato

D) TONO NARRATIVO
   - Nostalgia/Origini (focus su "come è nato", "eredità")
   - Attualità (focus su "sta accadendo ora", "emergente")
   - Tecnico/Descrittivo (focus su sound, caratteristiche)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
RILEVAMENTO CLUSTERING (bias sistematico)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Dopo l'analisi, conta:
- Quanti generi si concentrano sulla stessa EPOCA?
- Quanti sulla stessa GEOGRAFIA?
- Quanti con lo stesso TONO?

SE noti che il 40% o più dei generi condivide la stessa caratteristica = CLUSTERING RILEVATO

🎯 REGOLA DI BILANCIAMENTO:
- Se c'è clustering su una caratteristica → oggi scegli CONSAPEVOLMENTE qualcosa di diverso
- Se i titoli sono già ben distribuiti → libertà totale

⛔ REGOLA ASSOLUTA - BLACKLIST:
NON scegliere MAI un genere presente nella lista sopra (né identico né estremamente simile).
- Ogni voce nella lista è un genere già trattato: il tuo tema deve essere ASSENTE dalla lista
- Non scegliere sottogeneri o varianti di un genere in lista (es: se c'è "Cumbia" → NON cumbia villera, cumbia sonidera, ecc.)
- Esempio: se in lista c'è "Amapiano" → NON scegliere Amapiano né altri generi sudafricani recenti strettamente correlati
- VERIFICA: il genere che scegli NON deve essere in lista né essere un sottogenere/variante di uno in lista

⚠️ CONTROLLO FINALE OBBLIGATORIO:
Prima di procedere, verifica che il genere scelto NON sia:
- Presente nella lista dei generi già trattati
- Un sottogenere o variante regionale di un genere in lista
- Un movimento strettamente correlato a un genere in lista

OBIETTIVO: Massima varietà nel tempo. Non convergere sempre sugli stessi pattern.
Il mondo musicale ha MIGLIAIA di temi diversi. Esplora davvero.

═══════════════════════════════════════════
STEP 2: CHI SEI E COSA FAI
═══════════════════════════════════════════

Sei un giornalista musicale appassionato che scrive per "Foundly", una rivista musicale contemporanea.

Il tuo stile: informato, coinvolgente, accessibile. Racconti storie musicali con precisione e passione.

LA TUA MISSIONE OGGI:
Scegli un tema musicale affascinante e scrivi un articolo (300-400 parole per lingua) che lo esplori. 
Poi presenta 20-50 artisti autenticamente rappresentativi del tema.

═══════════════════════════════════════════
STEP 3: SCEGLI IL TEMA DI OGGI
═══════════════════════════════════════════

DEFINIZIONE DI "VARIETÀ" - Ruota consapevolmente tra:

1. SCENE STORICHE CONCLUSE
   - Movimenti del passato con eredità definita e impatto significativo
   - Preferisci movimenti che hanno lasciato un'eredità importante nella storia della musica

2. MOVIMENTI CONTEMPORANEI ATTIVI
   - Scene in corso con artisti attivi oggi
   // COMMENTATO: più libertà
   // - Usa con moderazione, preferendo comunque movimenti più consolidati e riconosciuti

3. GENERI REINTERPRETATI
   - Tradizioni storiche rilette dal presente
   // COMMENTATO: più libertà
   // - Preferisci quando la reinterpretazione ha prodotto artisti riconosciuti e influenti

4. SCENE EMERGENTI RECENTI
   - Movimenti nati negli ultimi 5-10 anni
   // COMMENTATO: più libertà
   // - Usa con moderazione: solo quando necessario per varietà, preferendo comunque movimenti più consolidati e riconosciuti

// COMMENTATO: più libertà nella scelta
// BILANCIAMENTO GENERI - Varia consapevolmente tra diverse categorie musicali, privilegiando movimenti storici importanti e riconosciuti:
// - Preferisci generi e movimenti che hanno avuto impatto significativo sulla storia della musica
// - Favorisci scene e movimenti che hanno prodotto artisti ampiamente riconosciuti
// - Bilancia tra diverse categorie musicali mantenendo focus su movimenti consolidati
// - Riduci l'enfasi su sottogeneri molto di nicchia o scene locali molto specifiche

// COMMENTATO: più libertà nella scelta
// ⚠️ **CRITICAL: EVITA GENERI TROPPO DI NICCHIA O SCONOSCIUTI**
// ⚠️ **Il tema deve avere ARTISTI VERIFICABILI su Spotify - se un genere è così di nicchia che non ha artisti su Spotify, NON sceglierlo**
// ⚠️ **Bello scoprire cose nuove, ma fino a un certo punto - deve essere possibile trovare almeno 20-30 artisti reali e verificabili**
// ⚠️ **NON scegliere generi così oscuri che non esistono artisti che li rappresentano su piattaforme musicali principali**

NON concentrarti sempre sugli stessi generi. Esplora liberamente tra generi diversi, tra epoche diverse, tra scene diverse. L'importante è che ci siano artisti verificabili e trovabili.

// COMMENTATO: più libertà nella scelta
// EQUILIBRIO SU DUE ASSI INDIPENDENTI:

// ASSE TEMPORALE:
// - 70% PASSATO (pre-2000): scene concluse, eredità definita, movimenti storici importanti
// - 30% PRESENTE (2000-oggi): scene attive, artisti contemporanei

// COMMENTATO: più libertà nella scelta
// ASSE POPOLARITÀ:
// - 80-85% MAINSTREAM: generi riconoscibili, artisti noti, movimenti musicali importanti e influenti
// - 15-20% UNDERGROUND: nicchia, sperimentale, sottoculture (solo quando necessario per varietà)

// COMMENTATO: più libertà nella scelta
// PRIORITÀ TEMATICA:
// - Preferisci movimenti musicali storici importanti che hanno avuto impatto significativo sulla cultura musicale
// - Favorisci generi e scene che hanno prodotto artisti riconosciuti, influenti e MAINSTREAM
// - Riduci l'enfasi su scene emergenti contemporanee molto di nicchia o locali
// - Bilancia tra grandi movimenti musicali del passato e scene contemporanee solo quando necessario per varietà
// - PRIORITÀ ASSOLUTA: scegli temi con artisti che la maggior parte delle persone conosce o ha sentito almeno una volta

ASSE GEOGRAFICO (varia consapevolmente):
- Nord America, Europa, Africa, Asia, Sud America, Oceania
- Se noti concentrazione eccessiva su UK/US → scegli altrove

Il tema DEVE permetterti di presentare artisti correlati autentici.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
VERIFICA PRIMA DI PROCEDERE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Dopo aver scelto il tema, FERMATI e chiediti:

1. Questo genere è presente nella lista dei generi già trattati (o è un sottogenere/variante)?
   → Se SÌ: CAMBIA tema immediatamente
   
2. Il tema è sufficientemente DIVERSO dalla maggioranza dei generi in lista?
   → Se NO: CAMBIA tema per bilanciare

3. Questo tema rappresenta un movimento musicale importante e riconosciuto?
   → Se NO: Considera movimenti più significativi e influenti

SOLO DOPO questa verifica → procedi con titolo e articolo.

═══════════════════════════════════════════
STEP 4: SCRIVI IL TITOLO
═══════════════════════════════════════════

FORMATO:
- Solo prima lettera maiuscola (tranne nomi propri)
- Massimo 60 caratteri
- Una frase fluida e naturale

VERIFICA STRUTTURA - Conta quanti titoli recenti iniziano con le stesse parole:
- Se noti ripetizione (es: molti "Quando...", molti "Come...") → USA STRUTTURA DIVERSA

CATEGORIE DI STRUTTURE (ruota tra queste):
1. DICHIARATIVE: "Il synth-pop ha conquistato gli anni '80"
2. INTERROGATIVE: "Perché il krautrock suona così ipnotico?"
3. NOMINALI: "Detroit, techno e il suono dei garage"
4. DESCRITTIVE: "La voce ruvida del grunge di Seattle"

Non convergere sempre sulla stessa. Varia consapevolmente.

═══════════════════════════════════════════
STEP 5: SCRIVI L'ARTICOLO IN INGLESE
═══════════════════════════════════════════

RICORDA: 300-400 PAROLE in inglese (vedi regola in alto).
Dopo aver scritto l'inglese, lo tradurrai/adatterai in italiano.

═══════════════════════════════════════════
STRUTTURA TRIPARTITA OBBLIGATORIA
═══════════════════════════════════════════

L'articolo DEVE essere diviso in 3 parti separate da \\n\\n:

1️⃣ APERTURA (80-100 parole)
   - Inizia con elemento concreto: anno, luogo, evento, persona, scena
   - Fornisci contesto essenziale: cosa stiamo esplorando e perché è interessante
   - Cattura l'attenzione immediatamente
   - Sii diretto e conciso - vai dritto al punto

2️⃣ SVILUPPO (150-200 parole) 
   - Il cuore del tema: cosa lo rende unico e speciale
   - QUESTA È LA PARTE PIÙ LUNGA: 100-130 parole
   - Un solo paragrafo o massimo 2 (usa \\n\\n per separarli se necessario)
   - Approfondisci: protagonisti chiave, luoghi principali, album importanti, evoluzione
   - FOCUS SU ARTISTI MAINSTREAM: menziona prima gli artisti più noti e riconoscibili
   
   NARRATIVA SDOPPIATA - Adatta al tema:
   
   PER TEMI STORICI (scene concluse):
   • Quando/dove è nato, protagonisti chiave più famosi
   • Come si è evoluto, momenti cruciali
   • Influenza su musica successiva
   • Usa PASSATO: "è nato", "ha trasformato", "erano"
   
   PER TEMI CONTEMPORANEI (scene attive):
   • Cosa sta emergendo ADESSO, chi lo sta facendo (focus su artisti mainstream)
   • Cosa lo rende unico OGGI
   • Nomi di artisti ATTUALMENTE ATTIVI e RICONOSCIBILI
   • Dove sta andando
   • Usa PRESENTE: "sta emergendo", "stanno ridefinendo", "è"
   
   ⚠️ REGOLA CRUCIALE: Se il tema ha artisti attivi oggi, parla di CHI lo fa ora, non solo delle origini.
   ⚠️ PRIORITÀ ARTISTI MAINSTREAM: menziona prima gli artisti più noti, poi eventualmente quelli meno conosciuti

3️⃣ CHIUSURA (50-80 parole)
   - Perché questo tema è rilevante/interessante oggi
   - Cosa lo rende speciale nel panorama musicale attuale
   - Lascia il lettore ispirato a esplorare
   - Sii conciso ma completo - 3-4 frasi ben sviluppate

TOTALE: 80-100 + 150-200 + 50-80 = 300-400 PAROLE

═══════════════════════════════════════════
STILE DI SCRITTURA
═══════════════════════════════════════════
- Accessibile e diretto: scrivi per un pubblico ampio, non solo per esperti
- Descrizioni sensoriali del SUONO (metafore concrete, NON generiche)
- Nomi SPECIFICI: luoghi, etichette, album, persone reali
  ❌ Generico: "i ritmi contagiosi", "è più di un semplice genere"
  ✅ Specifico: "il log drum di Kabza De Small", "l'album 'Scorpion Kings' del 2019"
- Scrivi come un giornalista esperto che racconta una storia vera, ma in modo accessibile
- EVITA frasi vuote come "è un fenomeno culturale", "sta conquistando il mondo"
- PREFERISCI fatti concreti: date, nomi, luoghi, album, tecniche di produzione
// COMMENTATO: più libertà nella scelta degli artisti
// - FOCUS SU ARTISTI MAINSTREAM: quando menzioni artisti, privilegia quelli più noti e riconoscibili
- ⛔ **REGOLA ASSOLUTA: NON ripetere MAI il titolo (o parti del titolo) nel testo dell'articolo**
  ❌ Se il titolo è "Il synth-pop ha conquistato gli anni '80" → NON scrivere "Il synth-pop ha conquistato..." nel testo
  ❌ NON iniziare l'introduzione con il titolo o pattern come "n/n Titolo" o "Titolo:"
  ❌ NON ripetere parole chiave del titolo nelle prime frasi (es: se il titolo contiene "cumbia", NON iniziare con "La cumbia...")
  ✅ Il testo deve iniziare direttamente con il contenuto, sviluppando il tema SENZA ripetere le parole esatte del titolo
  ✅ Usa sinonimi o descrizioni alternative invece di ripetere le parole del titolo
  ✅ Esempio: Se il titolo è "Il ritmo vibrante della cumbia", NON iniziare con "La cumbia è un ritmo..." ma con "Nata sulla costa caraibica..." o "Questo genere musicale..."

═══════════════════════════════════════════
STEP 6: TRADUCI/ADATTA IN ITALIANO
═══════════════════════════════════════════

Ora traduci l'articolo che hai appena scritto in inglese. Non riscrivere da zero, traduci fedelmente quello che hai scritto sopra.

Scrivi in italiano fluido e naturale, evitando i calchi linguistici dall'inglese. Le costruzioni sintattiche devono essere autenticamente italiane, come se l'articolo fosse stato scritto originariamente in italiano.

Per quanto riguarda i termini musicali, mantieni in inglese i nomi di generi e parole tecniche che sono comprensibili e di uso comune anche in italiano.

Se incontri metafore o espressioni idiomatiche inglesi che suonerebbero artificiali in italiano, adattale trovando equivalenti italiani che mantengano lo stesso impatto emotivo.

═══════════════════════════════════════════
STEP 7: SELEZIONA GLI ARTISTI (20-50)
═══════════════════════════════════════════

Cura una lista che rappresenti autenticamente il tema scelto.

INCLUDI:
- I più rappresentativi del tema (famosi e/o fondamentali)
- I sottovalutati che meritano attenzione
- Le gemme nascoste interessanti
- Mix intelligente: nomi riconoscibili + scoperte

SE IL TEMA È STORICO: includi sia i pionieri che chi continua oggi quella tradizione
SE IL TEMA È CONTEMPORANEO: focus su chi lo sta facendo ADESSO

VARIETÀ GEOGRAFICA: non concentrarti solo su una nazione.

⚠️ **CRITICAL: ONLY REAL ARTISTS THAT ACTUALLY EXIST - DO NOT INVENT ARTISTS - VERIFY EACH ARTIST EXISTS BEFORE INCLUDING THEM**
⚠️ **DO NOT CREATE FAKE ARTIST NAMES - ONLY USE REAL, VERIFIABLE ARTISTS**
⚠️ **IF YOU ARE NOT 100% SURE AN ARTIST EXISTS, DO NOT INCLUDE THEM**
⚠️ **NEVER INVENT ARTIST NAMES - THIS IS STRICTLY FORBIDDEN**
⚠️ Solo artisti REALI e AUTENTICAMENTE collegati al tema. 
⚠️ NON inserire artisti a caso o vagamente correlati.

═══════════════════════════════════════════
STEP 8: FORMATO JSON
═══════════════════════════════════════════

Rispondi SOLO con JSON puro. Niente markdown, niente commenti prima/dopo.

TECNICO - FORMATTAZIONE OBBLIGATORIA:
- Paragrafi separati con \\n\\n (backslash-n backslash-n) - DUE backslash-n, NON uno solo
- NON usare a capo reali dentro le stringhe JSON
- Stringhe su singola linea (tutto il testo in una riga)
- Escape virgolette se necessario (usare \\" per virgolette dentro il testo)
- NON includere markdown, asterischi, o formattazione speciale
- NON includere il titolo nel testo (il titolo va solo nel campo "title_en" e "title_it")
- Il testo deve essere testo puro, senza prefissi come "Titolo:" o numerazioni

STRUTTURA (INGLESE + ITALIANO):
{
  "title_en": "English title max 60 chars",
  "title_it": "Titolo italiano max 60 caratteri (TRADUZIONE di title_en)",
  "introduction_en": "Paragraph 1.\\n\\nParagraph 2.\\n\\nParagraph 3.",
  "introduction_it": "Paragrafo 1.\\n\\nParagrafo 2.\\n\\nParagrafo 3. (TRADUZIONE/ADATTAMENTO di introduction_en)",
  "theme_category": "Nome SPECIFICO del genere/scena (1-3 parole, es: Crossover Thrash, Moombahton, Grunge). NON usare solo la categoria ampia (es. NON solo Metal, Electronic, Rock).",
  "tags": ["tag1", "tag2", "tag3", "tag4"],
  "artists": [
    {"name": "Artist 1", "genre": "Genre", "origin": "Country"},
    {"name": "Artist 2", "genre": "Genre", "origin": "Country"},
    ...
  ]
}

IMPORTANTE: introduction_it DEVE essere la traduzione/adattamento accurato di introduction_en (non un articolo diverso).

═══════════════════════════════════════════
STEP 8: VERIFICA FINALE (prima di rispondere)
═══════════════════════════════════════════

CHECKLIST OBBLIGATORIA - Rispondi a TUTTE queste domande:

⛔ ANTI-RIPETIZIONE - VERIFICA OBBLIGATORIA:
✓ Il genere scelto NON è presente nella lista dei generi già trattati?
✓ Il genere scelto NON è un sottogenere/variante di uno in lista?
✓ Ho verificato che il mio genere NON è in lista? (es: se "Cumbia" è in lista → NON cumbia o varianti)
✓ Se c'era clustering su epoca/geografia, ho scelto qualcosa di diverso?
✓ Il mio tema è COMPLETAMENTE DIVERSO da tutti i generi in lista?

📝 TITOLO:
✓ Il titolo usa una struttura diversa da quelle ripetute nei titoli recenti?
✓ È una frase fluida e naturale? Solo prima lettera maiuscola?

📊 LUNGHEZZA ARTICOLI (CRITICO):
✓ Ho CONTATO le parole? EN: [___] IT: [___] 
✓ ENTRAMBI hanno 300-400 parole? (Se NO → NON rispondere, CORREGGI la lunghezza)

🇮🇹 QUALITÀ TRADUZIONE:
✓ Ho tradotto/adattato l'inglese (non riscritto da zero)?
✓ L'italiano suona naturale (no calchi dall'inglese)?
✓ Ho mantenuto la stessa lunghezza dell'inglese?

📚 CONTENUTO:
✓ Se il tema è contemporaneo, parlo di artisti ATTIVI OGGI?
✓ Se il tema è storico, uso narrativa al passato appropriata?
✓ Gli artisti sono autenticamente rappresentativi del tema?
✓ Il tema scelto ha abbastanza artisti verificabili su Spotify (almeno 20-30)? NON è troppo di nicchia?
✓ NON ho ripetuto il titolo (o parti del titolo) nel testo dell'articolo?
✓ Ho verificato che il mio genere NON è presente nella lista dei generi già trattati?
✓ Ho verificato che NON è un sottogenere o variante di un genere in lista?
✓ Il testo NON inizia con parole chiave del titolo? (es: se titolo contiene "cumbia", testo NON inizia con "La cumbia...")
✓ Ho usato sinonimi/descrizioni alternative invece di ripetere parole del titolo?

💻 FORMATO JSON:
✓ Ho usato \\n\\n per separare i paragrafi nel JSON?
✓ Le stringhe sono su una sola linea?

Se ANCHE UNA SOLA risposta è NO → CORREGGI prima di generare il JSON.

Sorprendici con qualcosa di speciale e DIVERSO.`;
  }

  /**
   * GENERA UN NUOVO DAILY DISCOVERY USANDO GEMINI AI
   * @returns {Promise<Object|null>} Discovery generata
   */
  async generateTodayDiscovery() {
    console.log('🚀 [GENERATE] Inizio generazione discovery...');
    
    try {
      // Usa configurazione da aiProvider.js (nome modello + label corretti)
      const config = getDailyDiscoveryConfig();
      const provider = config.provider;

      let GEMINI_MODELS = [];
      if (provider === 'deepseek') {
        const modelName = config.model;
        GEMINI_MODELS = [{ name: modelName, label: `DeepSeek ${modelName}`, version: 'v1' }];
      } else {
        // Gemini: usa getModelsForContext per label corretti da GEMINI_MODELS_CONFIG
        GEMINI_MODELS = getModelsForContext('dailyDiscovery');
        if (GEMINI_MODELS.length === 0) {
          GEMINI_MODELS = [
            { name: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', version: 'v1beta' },
            { name: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (v1)', version: 'v1' }
          ];
        }
      }
      console.log(`[GENERATE] Using ${provider} with model:`, GEMINI_MODELS[0].label);

      // Recupera TUTTE le theme_category già usate (lista generi già trattati)
      console.log('📚 [GENERATE] Recupero tutti i generi già trattati...');
      const { data: allRows, error: fetchError } = await supabase
        .from('daily_discoveries')
        .select('theme_category')
        .order('discovery_date', { ascending: false });
      if (fetchError) {
        console.error('[GENERATE] ❌ Errore recupero generi:', fetchError);
      }
      const raw = allRows || [];
      // Deduplica case-insensitive: "Motown" e "motown" = stesso genere
      const seen = new Set();
      const existingCategories = raw
        .map((r) => r.theme_category && r.theme_category.trim())
        .filter((c) => {
          if (!c) return false;
          const lower = c.toLowerCase();
          if (seen.has(lower)) return false;
          seen.add(lower);
          return true;
        });
      console.log(`[GENERATE] Generi già trattati: ${existingCategories.length}`, existingCategories.slice(0, 25));
      
      // Seleziona il prompt in base alla versione - OTTIMIZZATO PER CACHE
      const promptData = PROMPT_VERSION === 2 
        ? this.buildPromptV2Optimized(existingCategories)
        : this.buildPromptV1Optimized(existingCategories);
      
      console.log(`📝 [GENERATE] Using Prompt V${PROMPT_VERSION} (ottimizzato per cache)`);
      // Prova tutti i modelli in cascata
      let data = null;
      let usedModel = null;
      let usedModelConfig = null; // Salva la configurazione del modello usato
      
      for (let i = 0; i < GEMINI_MODELS.length; i++) {
        const model = GEMINI_MODELS[i];
        const callStartTime = Date.now();
        
        try {
          console.log(`📡 [GENERATE] Trying ${model.label}...`);

          geminiLogger.logApiCall({
            context: 'daily_discovery',
            model: model.label,
            modelName: model.name,
            apiVersion: model.version || 'v1beta',
            attempt: i + 1,
            totalModels: GEMINI_MODELS.length,
            recentThemesCount: existingCategories.length
          });

      // Gemini 2.5 usa "thinking mode" interno che consuma molti token
      // Serve più spazio per thinking + output (articoli lunghi richiedono più token)
      // Usa il massimo supportato: 65536 token per Gemini 2.5 Flash
      const maxTokens = model.name.includes('2.5') ? 65536 : 16384;
      
      // Chiama tramite proxy con systemInstruction separato (cache optimization)
      const proxyData = await callGeminiViaProxy({
        model: model.name,
        version: model.version || 'v1beta',
        systemInstruction: promptData.systemInstruction,
        prompt: promptData.userMessage, // Solo parte dinamica (data + recentThemes)
        generationConfig: {
          temperature: 0.8,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: maxTokens,
        },
        context: 'daily_discovery',
        keyLabel: `${model.label}-daily`,
        provider
      });

          const responseTime = Date.now() - callStartTime;
          console.log(`📥 [GENERATE] Proxy response received (${responseTime}ms)`);

      if (!proxyData) {
        // Proxy fallito, prova il prossimo modello
        console.log(`⚠️ [GENERATE] ${model.label} proxy failed, trying next model...`);
        
        geminiLogger.logApiError({
          context: 'daily_discovery',
          model: model.label,
          responseTime,
          errorMessage: 'Proxy call returned null'
        });
        
        if (i < GEMINI_MODELS.length - 1) {
          geminiLogger.logModelFallback({
            context: 'daily_discovery',
            fromModel: model.label,
            toModel: GEMINI_MODELS[i + 1].label,
            reason: 'proxy_failed'
          });
        }
        
        continue;
      }

      // Verifica se c'è un errore nella risposta del proxy
      if (proxyData.error) {
        const errorData = proxyData.error;
        const errorStatus = proxyData.status || 500;
        
        geminiLogger.logApiError({
          context: 'daily_discovery',
          model: model.label,
          status: errorStatus,
          errorData,
          responseTime,
          errorMessage: typeof errorData === 'string' ? errorData : errorData.message || 'Unknown error'
        });
        
        // Se quota esaurita (429 o 403), prova il prossimo modello
        if (errorStatus === 429 || errorStatus === 403) {
          console.log(`⚠️ [GENERATE] ${model.label} quota exceeded, trying next model...`);
          
          geminiLogger.logRateLimit({
            context: 'daily_discovery',
            model: model.label,
            status: errorStatus,
            attempt: i + 1,
            willRetry: i < GEMINI_MODELS.length - 1
          });
          
          if (i < GEMINI_MODELS.length - 1) {
            geminiLogger.logModelFallback({
              context: 'daily_discovery',
              fromModel: model.label,
              toModel: GEMINI_MODELS[i + 1].label,
              reason: 'rate_limit'
            });
          }
          
          continue;
        }
        
        // Altri errori
        console.error(`❌ [GENERATE] ${model.label} error:`, errorStatus, errorData);
        if (i === GEMINI_MODELS.length - 1) {
          throw new Error(`Gemini API error: ${errorStatus} - ${typeof errorData === 'string' ? errorData : errorData.message || 'Unknown error'}`);
        }
        continue;
      }

          data = proxyData;
          usedModel = model.label;
          usedModelConfig = { ...model, provider }; // Salva il config per rigenerazioni incluso provider
          const successTime = Date.now() - callStartTime;
          
          console.log(`✅ [GENERATE] Successfully used ${model.label}`);
          
          geminiLogger.logApiSuccess({
            context: 'daily_discovery',
            model: model.label,
            responseTime: successTime,
            usageMetadata: data.usageMetadata,
            promptTokens: data.usageMetadata?.promptTokenCount,
            candidatesTokens: data.usageMetadata?.candidatesTokenCount,
            totalTokens: data.usageMetadata?.totalTokenCount
          });
          
          break; // Successo! Esci dal loop
          
        } catch (error) {
          const errorTime = Date.now() - callStartTime;
          console.error(`❌ [GENERATE] ${model.label} failed:`, error.message);
          
          geminiLogger.logApiError({
            context: 'daily_discovery',
            model: model.label,
            responseTime: errorTime,
            errorMessage: error.message,
            errorStack: error.stack,
            attempt: i + 1,
            willRetry: i < GEMINI_MODELS.length - 1
          });
          
          // Se c'è un prossimo modello, logga il fallback
          if (i < GEMINI_MODELS.length - 1) {
            geminiLogger.logModelFallback({
              context: 'daily_discovery',
              fromModel: model.label,
              toModel: GEMINI_MODELS[i + 1].label,
              reason: 'exception',
              errorMessage: error.message
            });
          }
          
          // Se è l'ultimo modello, rilancia l'errore
          if (i === GEMINI_MODELS.length - 1) {
            throw error;
          }
          
          // Altrimenti continua con il prossimo
          console.log(`🔄 [GENERATE] Trying next model...`);
          continue;
        }
      }
      
      if (!data) {
        throw new Error('All Gemini models failed');
      }
      
      // Log modello utilizzato con successo
      console.log(`🤖 [GENERATE] Used model: ${usedModel}`);
      if (data.usageMetadata) {
        console.log('📊 [GENERATE] Token usati:', data.usageMetadata);
      }
      
      // Log completo della risposta per debugging
      console.log('🔍 [GENERATE] Struttura risposta Gemini:', JSON.stringify(data, null, 2).substring(0, 1000));
      
      const textContent = data.candidates?.[0]?.content?.parts?.[0]?.text;
      const finishReason = data.candidates?.[0]?.finishReason;
      
      if (!textContent) {
        console.error('❌ [GENERATE] Nessun contenuto nella risposta Gemini');
        console.error('📋 [GENERATE] Risposta completa:', JSON.stringify(data, null, 2));
        
        // Diagnosi specifica per MAX_TOKENS
        if (finishReason === 'MAX_TOKENS') {
          const thoughtsTokens = data.usageMetadata?.thoughtsTokenCount || 0;
          const totalTokens = data.usageMetadata?.totalTokenCount || 0;
          
          console.error(`❌ [GENERATE] ${usedModel} hit MAX_TOKENS: ${thoughtsTokens} thinking tokens, ${totalTokens} total`);
          console.error(`💡 [GENERATE] Aumenta maxOutputTokens o semplifica il prompt`);
          
          geminiLogger.logApiError({
            context: 'daily_discovery',
            model: usedModel,
            errorMessage: 'MAX_TOKENS: thinking consumed all available tokens',
            finishReason,
            thoughtsTokenCount: thoughtsTokens,
            totalTokenCount: totalTokens,
            responseStructure: JSON.stringify(data, null, 2).substring(0, 500)
          });
        }
        
        throw new Error(`Nessun contenuto nella risposta Gemini (finishReason: ${finishReason || 'unknown'})`);
      }
      console.log('📄 [GENERATE] Testo ricevuto (primi 300 caratteri):', textContent.substring(0, 300) + '...');

      // Pulizia e parsing della risposta JSON
      let cleanedText = textContent.trim();
      
      // Rimuovi markdown code blocks
      cleanedText = cleanedText.replace(/```json\n?/g, '');
      cleanedText = cleanedText.replace(/```\n?/g, '');
      cleanedText = cleanedText.trim();
      
      console.log('🧹 [GENERATE] Testo ricevuto (primi 500 caratteri):', cleanedText.substring(0, 500) + '...');

      let discoveryData;
      try {
        // Primo tentativo: prova a parsare così com'è
        discoveryData = JSON.parse(cleanedText);
        console.log('✅ [GENERATE] JSON parsato al primo tentativo');
        
        geminiLogger.logJsonParsing({
          context: 'daily_discovery',
          model: usedModel,
          success: true,
          attempt: 1,
          textLength: cleanedText.length
        });
        
      } catch (parseError) {
        console.log('⚠️ [GENERATE] Primo parsing fallito, provo a estrarre JSON dal testo...');
        
        // Tentativo 2: Estrai JSON dal testo (Gemini 2.5 a volte aggiunge testo narrativo)
        try {
          const firstBrace = cleanedText.indexOf('{');
          const lastBrace = cleanedText.lastIndexOf('}');
          
          if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
            const jsonString = cleanedText.substring(firstBrace, lastBrace + 1);
            discoveryData = JSON.parse(jsonString);
            
            console.log('✅ [GENERATE] JSON estratto con successo (era dentro altro contenuto)');
            
            geminiLogger.logJsonParsing({
              context: 'daily_discovery',
              model: usedModel,
              success: true,
              attempt: 2,
              textLength: cleanedText.length,
              jsonExtracted: true,
              method: 'brace_extraction'
            });
          } else {
            throw new Error('No valid JSON structure found');
          }
          
        } catch (extractError) {
          // Tentativo 3: Pulisci caratteri di controllo
          console.log('⚠️ [GENERATE] Estrazione fallita, provo a pulire caratteri di controllo...');
          
          let cleanedText2 = cleanedText.replace(/[\x00-\x1F]/g, ' ');
          cleanedText2 = cleanedText2.replace(/\s+/g, ' ');
          
          try {
            discoveryData = JSON.parse(cleanedText2);
          console.log('✅ [GENERATE] JSON parsato dopo pulizia caratteri di controllo');
            
            geminiLogger.logJsonParsing({
              context: 'daily_discovery',
              model: usedModel,
              success: true,
              attempt: 3,
              textLength: cleanedText2.length,
              cleaningApplied: 'control_characters_removed'
            });
            
          } catch (thirdError) {
            console.error('❌ [GENERATE] ERRORE nel parsing JSON (tutti i tentativi falliti)');
            console.error('📄 [GENERATE] Testo problematico (primi 800 char):', cleanedText.substring(0, 800));
            console.error('📄 [GENERATE] Testo problematico (ultimi 200 char):', cleanedText.substring(Math.max(0, cleanedText.length - 200)));
            
            geminiLogger.logJsonParsing({
              context: 'daily_discovery',
              model: usedModel,
              success: false,
              attempt: 3,
              error: `All parsing attempts failed. Last: ${thirdError.message}`,
              textSample: cleanedText.substring(0, 800),
              textEnd: cleanedText.substring(Math.max(0, cleanedText.length - 200))
            });
            
            throw new Error(`Failed to parse Gemini JSON response after all attempts: ${thirdError.message}`);
          }
        }
      }

      // ============================================
      // POST-PROCESSING: VERIFICA LUNGHEZZA ARTICOLI
      // ============================================
      
      const MIN_WORDS = 280; // Minimo accettabile (margine sotto 300)
      const MAX_WORDS = 450; // Massimo accettabile (margine sopra 400)
      
      const wordsEn = this.countWords(discoveryData.introduction_en);
      const wordsIt = this.countWords(discoveryData.introduction_it);
      
      console.log(`📊 [GENERATE] Conteggio parole - EN: ${wordsEn}, IT: ${wordsIt}`);
      
      // Se l'articolo è troppo corto, rigenera automaticamente
      if (wordsEn < MIN_WORDS || wordsIt < MIN_WORDS) {
        const shortArticle = wordsEn < MIN_WORDS ? discoveryData.introduction_en : discoveryData.introduction_it;
        const shortLanguage = wordsEn < MIN_WORDS ? 'en' : 'it';
        
        console.warn(`⚠️ [GENERATE] Articolo troppo corto (EN: ${wordsEn}, IT: ${wordsIt} parole) - RIGENERAZIONE AUTOMATICA...`);
        
        try {
          // Rigenera l'articolo corto
          const regenerationPrompt = this.buildRegenerationPrompt(
            shortArticle,
            discoveryData.theme_category,
            discoveryData.title_en
          );
          
          console.log(`🔄 [GENERATE] Rigenerazione articolo ${shortLanguage.toUpperCase()}...`);
          
          // Usa lo stesso modello che ha funzionato prima
          const apiVersion = usedModelConfig?.version || 'v1beta';
          const modelName = usedModelConfig?.name || 'gemini-2.0-flash';
          const maxTokens = modelName.includes('2.5') ? 65536 : 16384;
          
          const regenData = await callGeminiViaProxy({
            model: modelName,
            version: apiVersion,
            prompt: regenerationPrompt,
            generationConfig: {
              temperature: 0.8,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: maxTokens,
            },
            context: 'daily_discovery',
            provider: usedModelConfig?.provider || 'gemini',
            keyLabel: `${usedModelConfig?.label || modelName}-regen`
          });
          
          if (!regenData || regenData.error) {
            throw new Error(`Rigenerazione fallita: ${regenData?.error || 'Proxy returned null'}`);
          }
          const regenText = regenData.candidates?.[0]?.content?.parts?.[0]?.text;
          
          if (!regenText) {
            throw new Error('Nessun contenuto nella risposta di rigenerazione');
          }
          
          // Pulisci il testo rigenerato
          let cleanedRegenText = regenText.trim();
          cleanedRegenText = cleanedRegenText.replace(/```json\n?/g, '');
          cleanedRegenText = cleanedRegenText.replace(/```\n?/g, '');
          cleanedRegenText = cleanedRegenText.trim();
          
          // Se è JSON, estrai solo il testo
          if (cleanedRegenText.startsWith('{')) {
            try {
              const regenJson = JSON.parse(cleanedRegenText);
              if (regenJson.introduction_en || regenJson.introduction_it) {
                // Se ha entrambi, usa quello corretto
                if (shortLanguage === 'en') {
                  discoveryData.introduction_en = regenJson.introduction_en || cleanedRegenText;
                } else {
                  discoveryData.introduction_it = regenJson.introduction_it || cleanedRegenText;
                }
              } else {
                // Se non è JSON valido, usa il testo così com'è
                if (shortLanguage === 'en') {
                  discoveryData.introduction_en = cleanedRegenText;
                } else {
                  discoveryData.introduction_it = cleanedRegenText;
                }
              }
            } catch {
              // Non è JSON, usa il testo così com'è
              if (shortLanguage === 'en') {
                discoveryData.introduction_en = cleanedRegenText;
              } else {
                discoveryData.introduction_it = cleanedRegenText;
              }
            }
          } else {
            // Testo puro, usa direttamente
            if (shortLanguage === 'en') {
              discoveryData.introduction_en = cleanedRegenText;
            } else {
              discoveryData.introduction_it = cleanedRegenText;
            }
          }
          
          // Se abbiamo rigenerato l'inglese, dobbiamo anche rigenerare l'italiano (traduzione)
          if (shortLanguage === 'en' && discoveryData.introduction_en) {
            console.log(`🔄 [GENERATE] Rigenerazione articolo IT (traduzione da EN)...`);
            
            try {
              const translationPrompt = `Traduci questo articolo musicale dall'inglese all'italiano. Mantieni lo stesso tono, stile e lunghezza (300-400 parole). Usa italiano fluido e naturale, evitando calchi linguistici.

ARTICOLO INGLESE:
${discoveryData.introduction_en}

Rispondi SOLO con l'articolo tradotto in italiano, senza altre spiegazioni. Usa \\n\\n per separare i paragrafi.`;

              const transData = await callGeminiViaProxy({
                model: modelName,
                version: apiVersion,
                prompt: translationPrompt,
                generationConfig: {
                  temperature: 0.7,
                  topK: 40,
                  topP: 0.95,
                  maxOutputTokens: maxTokens,
                },
                context: 'daily_discovery',
                provider: usedModelConfig?.provider || 'gemini',
                keyLabel: `${usedModelConfig?.label || modelName}-translation`
              });
              
              if (transData && !transData.error) {
                const transText = transData.candidates?.[0]?.content?.parts?.[0]?.text;
                
                if (transText) {
                  let cleanedTransText = transText.trim();
                  cleanedTransText = cleanedTransText.replace(/```json\n?/g, '');
                  cleanedTransText = cleanedTransText.replace(/```\n?/g, '');
                  cleanedTransText = cleanedTransText.trim();
                  
                  discoveryData.introduction_it = cleanedTransText;
                  console.log(`✅ [GENERATE] Traduzione IT completata`);
                }
              }
            } catch (transError) {
              console.error('❌ [GENERATE] Errore durante traduzione IT:', transError);
              // Continua senza traduzione
            }
          }
          
          // Riconta le parole dopo la rigenerazione
          const newWordsEn = this.countWords(discoveryData.introduction_en);
          const newWordsIt = this.countWords(discoveryData.introduction_it);
          
          console.log(`✅ [GENERATE] Dopo rigenerazione - EN: ${newWordsEn}, IT: ${newWordsIt} parole`);
          
          if (newWordsEn < MIN_WORDS || newWordsIt < MIN_WORDS) {
            console.warn(`⚠️ [GENERATE] Articolo ancora troppo corto dopo rigenerazione (EN: ${newWordsEn}, IT: ${newWordsIt} parole)`);
          } else {
            console.log('✅ [GENERATE] Articolo rigenerato con successo!');
          }
          
        } catch (regenError) {
          console.error('❌ [GENERATE] Errore durante rigenerazione:', regenError);
          console.warn('⚠️ [GENERATE] Procedo con articolo corto (verrà salvato comunque)');
        }
      } else if (wordsEn > MAX_WORDS || wordsIt > MAX_WORDS) {
        console.warn(`⚠️ [GENERATE] ATTENZIONE: Articoli sopra il massimo (EN: ${wordsEn}, IT: ${wordsIt} parole)`);
      } else {
        console.log('✅ [GENERATE] Lunghezza articoli OK!');
      }

      // Salvataggio su Supabase
      const now = new Date();
      const discoveryTimestamp = now.toISOString();
      console.log('💾 [GENERATE] Salvataggio su Supabase con timestamp:', discoveryTimestamp);
      
      // Verifica sessione Supabase per debug RLS
      const { data: { session } } = await supabase.auth.getSession();
      console.log('🔐 [GENERATE] Sessione Supabase:', session ? `autenticato (${session.user?.email || 'no email'})` : 'nessuna sessione (anon key)');

      // Inserisci discovery
      const { data: savedDiscovery, error: saveError } = await supabase
        .from('daily_discoveries')
        .insert({
          discovery_date: discoveryTimestamp,
          title: discoveryData.title_en,
          title_en: discoveryData.title_en,
          title_it: discoveryData.title_it,
          introduction: discoveryData.introduction_en,
          introduction_en: discoveryData.introduction_en,
          introduction_it: discoveryData.introduction_it,
          theme_category: discoveryData.theme_category,
          tags: discoveryData.tags || [],
          artist_count: discoveryData.artists?.length || 0
        })
        .select()
        .single();

      if (saveError) {
        console.error('❌ [GENERATE] Errore salvataggio discovery:', saveError);
        throw saveError;
      }
      console.log('✅ [GENERATE] Discovery salvata con ID:', savedDiscovery.id);

      // FILTRAGGIO A MONTE: verifica artisti su Deezer PRIMA di salvare
      if (discoveryData.artists && discoveryData.artists.length > 0) {
        console.log(`🔍 [GENERATE] Verificando ${discoveryData.artists.length} artisti su Deezer...`);
        
        const BATCH_SIZE = 5; // Deezer proxy: batch piccoli per evitare sovraccarico
        const verifiedArtists = [];
        
        for (let i = 0; i < discoveryData.artists.length; i += BATCH_SIZE) {
          const batch = discoveryData.artists.slice(i, i + BATCH_SIZE);
          console.log(`🔍 [GENERATE] Verificando batch ${Math.floor(i/BATCH_SIZE) + 1}/${Math.ceil(discoveryData.artists.length/BATCH_SIZE)} (${batch.length} artisti)`);
          
          const batchResults = await Promise.all(
            batch.map(async (artist) => {
              const artistName = typeof artist === 'string' ? artist : artist.name;
              const artistGenre = typeof artist === 'object' ? artist.genre : null;
              const artistOrigin = typeof artist === 'object' ? artist.origin : null;
              
              try {
                const deezerArtist = await searchDeezerArtist(artistName, artistGenre, artistOrigin);
                
                if (deezerArtist && deezerArtist.id) {
                  console.log(`✅ [GENERATE] "${artistName}" verificato su Deezer (id: ${deezerArtist.id})`);
                  return {
                    name: artistName,
                    genre: artistGenre,
                    origin: artistOrigin,
                    deezer_id: String(deezerArtist.id),
                    image_url: deezerArtist.images?.[0]?.url || null,
                    verified: true
                  };
                } else {
                  console.log(`❌ [GENERATE] "${artistName}" NON trovato su Deezer - escluso`);
                  return null;
                }
              } catch (error) {
                console.warn(`⚠️ [GENERATE] Errore verifica "${artistName}":`, error.message);
                return null;
              }
            })
          );
          
          const validFromBatch = batchResults.filter(a => a !== null);
          verifiedArtists.push(...validFromBatch);
          
          // Pausa tra batch per non sovraccaricare il proxy
          if (i + BATCH_SIZE < discoveryData.artists.length) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
        
        console.log(`✅ [GENERATE] Verificati: ${verifiedArtists.length}/${discoveryData.artists.length} artisti validi`);
        
        // Salva SOLO gli artisti verificati
        if (verifiedArtists.length > 0) {
          console.log(`💾 [GENERATE] Salvataggio di ${verifiedArtists.length} artisti verificati...`);
          
          const artistsToInsert = verifiedArtists.map((artist, index) => ({
            discovery_id: savedDiscovery.id,
            artist_name: artist.name,
            artist_genre: artist.genre,
            artist_origin: artist.origin,
            spotify_id: `deezer:${artist.deezer_id}`, // Formato unificato
            spotify_verified: true,
            position: index + 1
          }));

          const { error: artistsError } = await supabase
            .from('daily_discovery_artists')
            .insert(artistsToInsert);

          if (artistsError) {
            console.error('❌ [GENERATE] Errore salvataggio artisti:', artistsError);
          } else {
            console.log('✅ [GENERATE] Artisti verificati salvati.');
            
            // Salva/aggiorna artisti nella tabella artists per i join futuri
            for (const artist of verifiedArtists) {
              const uniqueKey = `deezer:${artist.deezer_id}`;
              await supabase
                .from('artists')
                .upsert({
                  spotify_id: uniqueKey,
                  deezer_id: artist.deezer_id,
                  name: artist.name,
                  image_url: artist.image_url || null,
                  genres: [],
                }, { onConflict: 'spotify_id', ignoreDuplicates: false });
            }
            console.log('✅ [GENERATE] Artisti sincronizzati nella tabella artists.');
            
            // Aggiorna artist_count con il numero reale di artisti verificati
            await supabase
              .from('daily_discoveries')
              .update({ artist_count: verifiedArtists.length })
              .eq('id', savedDiscovery.id);
          }
        } else {
          console.warn('⚠️ [GENERATE] Nessun artista verificato! Discovery salvata senza artisti.');
        }
      }

      console.log('🎉 [GENERATE] Discovery generata con successo!');
      
      geminiLogger.logPerformance({
        context: 'daily_discovery',
        model: usedModel,
        success: true,
        artistCount: discoveryData.artists?.length || 0,
        discoveryId: savedDiscovery.id,
        themeCategory: discoveryData.theme_category
      });
      
      return {
        ...savedDiscovery,
        artists: discoveryData.artists
      };

    } catch (error) {
      console.error('💥 [GENERATE] ERRORE COMPLETO NEL PROCESSO:', error);
      console.error('Stack trace:', error.stack);
      
      geminiLogger.logApiError({
        context: 'daily_discovery_complete',
        errorMessage: error.message,
        errorStack: error.stack,
        phase: 'complete_process'
      });
      
      throw error;
    }
  }
}

export default new DailyDiscoveryService();
